/**
 * AppError — global 500 component, wired as `defaultErrorComponent` on the
 * router (see `apps/web/src/main.tsx`).
 *
 * Standards:
 * - State the failure plainly; don't dump raw stack frames as the headline.
 * - Offer a deterministic retry (`reset()` re-renders the failed boundary)
 *   and an escape hatch (back to workspace).
 * - In dev, reveal `error.message` + the stack inside a `<details>` so the
 *   developer doesn't have to switch panes. In prod the same `<details>` shows
 *   only `error.message` — Sentry already captured the full trace; we don't
 *   need to leak internals to the user but a one-line cause helps them
 *   describe the issue to support.
 */
import * as React from "react";
import { Link, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { Button } from "@flint/ui";
import { ErrorScreen } from "./error-screen.js";

export function AppError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const isDev = import.meta.env.DEV;

  // Best-effort message extraction. `error` is typed `Error` but in practice
  // anything thrown can land here, including non-Error values from loaders.
  const message = extractMessage(error);
  const stack = error instanceof Error ? error.stack : undefined;

  const handleRetry = React.useCallback(() => {
    // `reset()` clears the error boundary; `invalidate()` re-runs loaders. We
    // want both: the page should re-render AND any failed query should retry.
    reset();
    void router.invalidate();
  }, [reset, router]);

  return (
    <ErrorScreen
      status={500}
      kicker="Something went wrong"
      tone="bad"
      title="We hit an unexpected error."
      description="The team has been notified. You can try again, or head back to the workspace and pick up where you left off."
      actions={
        <>
          <Button type="button" intent="primary" onClick={handleRetry}>
            Try again
          </Button>
          <Button asChild intent="ghost">
            <Link to="/">Back to workspace</Link>
          </Button>
        </>
      }
      detail={
        message || stack ? (
          <details className="group mt-2">
            <summary className="cursor-pointer text-xs text-muted hover:text-ink-2 select-none list-none [&::-webkit-details-marker]:hidden">
              <span className="inline-flex items-center gap-1.5">
                <ChevronRight
                  size={10}
                  strokeWidth={2}
                  aria-hidden
                  className="transition-transform group-open:rotate-90"
                />
                Technical details
              </span>
            </summary>
            <div className="mt-3 flex flex-col gap-2">
              {message ? (
                <pre className="text-xs leading-[17px] text-ink-2 whitespace-pre-wrap break-words font-mono bg-raised border border-line rounded-sm p-3">
                  {message}
                </pre>
              ) : null}
              {isDev && stack ? (
                <pre className="text-xs leading-[16px] text-muted whitespace-pre-wrap break-words font-mono bg-raised border border-line rounded-sm p-3 max-h-64 overflow-auto">
                  {stack}
                </pre>
              ) : null}
            </div>
          </details>
        ) : null
      }
    />
  );
}

function extractMessage(value: unknown): string {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "message" in value) {
    const m = (value as { message: unknown }).message;
    if (typeof m === "string") return m;
  }
  return "";
}
